<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <div class="page-head">
      <div>
        <h2>收款流水</h2>
        <p>支持一笔成交分次收款；确认后进入资金账户，冲销生成反向流水</p>
      </div>
      <el-button
        type="primary"
        @click="openCreate"
      >
        登记收款
      </el-button>
    </div>
    <el-card shadow="never">
      <div class="filters">
        <el-select
          v-model="status"
          clearable
          placeholder="全部状态"
          @change="reload"
        >
          <el-option
            label="草稿"
            value="DRAFT"
          />
          <el-option
            label="已确认"
            value="CONFIRMED"
          />
          <el-option
            label="已冲销"
            value="VOID"
          />
        </el-select>
        <el-button
          :loading="loading"
          @click="load"
        >
          刷新
        </el-button>
      </div>
      <el-table
        v-loading="loading"
        :data="rows"
        stripe
      >
        <el-table-column
          prop="receipt_date"
          label="收款日期"
          width="170"
        />
        <el-table-column
          prop="transaction_id"
          label="成交编号"
          width="100"
        />
        <el-table-column
          prop="customer_name"
          label="客户"
          min-width="120"
        />
        <el-table-column
          prop="housing_number"
          label="房源编号"
          min-width="120"
        />
        <el-table-column
          label="金额"
          width="130"
        >
          <template #default="{ row }">¥ {{ money(row.amount) }}</template>
        </el-table-column>
        <el-table-column
          prop="account_name"
          label="资金账户"
          width="130"
        />
        <el-table-column
          prop="reference_no"
          label="流水号"
          min-width="130"
        />
        <el-table-column
          label="状态"
          width="100"
        >
          <template #default="{ row }">
            <StatusTag :status="row.status" />
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="210"
          fixed="right"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'DRAFT'"
              link
              type="success"
              @click="confirmRow(row)"
            >
              确认
            </el-button>
            <el-button
              v-if="row.status === 'CONFIRMED'"
              link
              type="danger"
              @click="voidRow(row)"
            >
              冲销
            </el-button>
            <VoucherButton
              biz-type="RECEIPT"
              :biz-id="row.id"
            />
          </template>
        </el-table-column>
      </el-table>
      <div class="pager">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          :total="total"
          @current-change="load"
        />
      </div>
    </el-card>
    <el-dialog
      v-model="dialog"
      title="登记收款"
      width="620px"
    >
      <el-form
        :model="form"
        label-width="110px"
      >
        <el-form-item
          label="应收成交"
          required
        >
          <el-select
            v-model="form.transactionCommissionId"
            filterable
            style="width: 100%"
            placeholder="选择尚有余额的成交"
          >
            <el-option
              v-for="item in receivables"
              :key="item.id"
              :label="`#${item.transaction_id} ${
                item.customer_name || ''
              }（未收¥${money(item.outstanding_amount)}）`"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="收款日期"
          required
        >
          <el-date-picker
            v-model="form.receiptDate"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item
          label="收款金额"
          required
        >
          <el-input-number
            v-model="form.amount"
            :min="0.01"
            :precision="4"
            :controls="false"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item
          label="资金账户"
          required
        >
          <el-select
            v-model="form.accountId"
            style="width: 100%"
          >
            <el-option
              v-for="item in accounts"
              :key="item.id"
              :label="`${item.name}（¥${money(item.balance)}）`"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select
            v-model="form.paymentMethod"
            style="width: 100%"
          >
            <el-option
              v-for="item in methods"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="流水号">
          <el-input v-model="form.referenceNo" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="form.note"
            type="textarea"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="save"
        >
          保存草稿
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ledgerApi, organizationApi } from '../api'
import StatusTag from '../components/StatusTag.vue'
import VoucherButton from '../components/VoucherButton.vue'

const rows = ref([])
const receivables = ref([])
const accounts = ref([])
const methods = ref([])
const loading = ref(false)
const saving = ref(false)
const dialog = ref(false)
const status = ref('')
const page = ref(1)
const pageSize = ref(20)
const total = ref(0)
const form = reactive({
  transactionCommissionId: null,
  receiptDate: '',
  amount: 0,
  accountId: null,
  paymentMethod: 'BANK',
  referenceNo: '',
  note: '',
})
const money = (value) =>
  Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
const now = () => new Date().toISOString().slice(0, 19).replace('T', ' ')
const load = async () => {
  loading.value = true
  try {
    const data = await ledgerApi.receipts({
      page: page.value,
      pageSize: pageSize.value,
      status: status.value,
    })
    rows.value = data.items || []
    total.value = data.total || 0
  } catch (error) {
    ElMessage.error(error?.message || '收款流水加载失败')
  } finally {
    loading.value = false
  }
}
const reload = () => {
  page.value = 1
  load()
}
const openCreate = async () => {
  const [receiveData, accountData, methodData] = await Promise.all([
    ledgerApi.receivables({ page: 1, pageSize: 100 }),
    ledgerApi.accounts(),
    organizationApi.paymentMethods(),
  ])
  receivables.value = (receiveData.items || []).filter(
    (item) => Number(item.outstanding_amount) > 0
  )
  accounts.value = accountData.filter((item) => item.enabled)
  methods.value = methodData.filter((item) => item.enabled)
  Object.assign(form, {
    transactionCommissionId: null,
    receiptDate: now(),
    amount: 0,
    accountId: accounts.value[0]?.id || null,
    paymentMethod: methods.value[0]?.code || 'BANK',
    referenceNo: '',
    note: '',
  })
  dialog.value = true
}
const save = async () => {
  if (
    !form.transactionCommissionId ||
    !form.receiptDate ||
    !form.amount ||
    !form.accountId
  )
    return ElMessage.warning('请完整填写应收成交、日期、金额和账户')
  saving.value = true
  try {
    await ledgerApi.createReceipt({ ...form })
    ElMessage.success('收款草稿已保存')
    dialog.value = false
    await load()
  } catch (error) {
    ElMessage.error(error?.message || '保存失败')
  } finally {
    saving.value = false
  }
}
const confirmRow = async (row) => {
  try {
    await ElMessageBox.confirm(
      '确认后将写入资金账户流水，是否继续？',
      '确认收款'
    )
    await ledgerApi.confirmReceipt(row.id)
    ElMessage.success('收款已确认')
    await load()
  } catch (error) {
    if (error !== 'cancel') ElMessage.error(error?.message || '确认失败')
  }
}
const voidRow = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入冲销原因', '冲销收款', {
      inputValidator: (text) => !!text || '原因不能为空',
    })
    await ledgerApi.voidReceipt(row.id, value)
    ElMessage.success('收款已冲销')
    await load()
  } catch (error) {
    if (error !== 'cancel') ElMessage.error(error?.message || '冲销失败')
  }
}
onMounted(load)
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-head h2 {
  font-size: 24px;
}
.page-head p {
  color: #8a97a8;
  margin-top: 6px;
}
.el-card {
  border: 0;
}
.filters {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}
.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}
</style>
